import {
  DesignTemplateData,
  EMPTY_TEMPLATE_DATA,
  SECTION_IDENTITY_FIELDS,
  TemplateElement,
  TemplatePlaceholder,
} from '@/types/designTemplate'

/**
 * Per-element fields that are either recomputed on apply or belong to the
 * authoring session rather than the template.
 *
 * `sectionId` is overwritten from the destination section, `index` is the array
 * position, the geometry trio is recalculated once the design is rescaled, and
 * `needToCheck` is a profanity flag that belongs to a customer's order.
 */
const TRANSIENT_ELEMENT_FIELDS = [
  'sectionId',
  'index',
  'outline',
  'snapping',
  'insideData',
  'visible',
  'lastUpdate',
  'needToCheck',
] as const

const stripKeys = (source: Record<string, any>, keys: readonly string[]): Record<string, any> => {
  const result: Record<string, any> = { ...source }
  keys.forEach((key) => delete result[key])
  return result
}

/**
 * Turns whatever the authoring editor produced into the portable payload we
 * store. A saved design describes a design sitting on one specific section, so
 * it names that section. A template has no home: it is applied away from
 * wherever it was authored, so every trace of that section is dropped here and
 * supplied by the destination at apply time.
 */
export const normalizeTemplateData = (raw: any): DesignTemplateData => {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_TEMPLATE_DATA }

  const source = raw.sections?.[0] && typeof raw.sections[0] === 'object' ? raw.sections[0] : raw

  const canvas =
    raw.canvas ||
    (typeof source.width === 'number' && typeof source.height === 'number'
      ? {
          width: source.width,
          height: source.height,
          offset_x: source.offset_x ?? 0,
          offset_Y: source.offset_Y ?? 0,
        }
      : undefined)

  const elements: TemplateElement[] = Array.isArray(source.elements)
    ? source.elements.map((element: any) => stripKeys(element, TRANSIENT_ELEMENT_FIELDS) as TemplateElement)
    : []

  const normalized: DesignTemplateData = {
    version: raw.version ?? 1,
    elements,
  }

  if (raw.origin) normalized.origin = raw.origin
  if (canvas) normalized.canvas = canvas

  return normalized
}

/** True when the payload still names a section it will be applied away from. */
export const carriesSectionIdentity = (data: any): boolean => {
  if (!data || typeof data !== 'object') return false
  return SECTION_IDENTITY_FIELDS.some((field) => field in data)
}

export const getPlaceholders = (data?: DesignTemplateData | null): TemplatePlaceholder[] => {
  if (!data?.elements?.length) return []

  return data.elements
    .map((element) => element.template)
    .filter((tag): tag is TemplatePlaceholder => !!tag && tag.placeholder === true)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

/**
 * A template with elements must say which canvas it was drawn against,
 * otherwise it cannot be rescaled onto a destination section.
 */
export const validateTemplateData = (data: DesignTemplateData): string | null => {
  if (carriesSectionIdentity(data)) {
    return 'The design still carries section details. It must be section independent to be reusable.'
  }

  if (data.elements.length > 0 && !data.canvas) {
    return 'The design is missing the canvas it was drawn against, so it cannot be resized onto another section.'
  }

  if (data.canvas && (data.canvas.width <= 0 || data.canvas.height <= 0)) {
    return 'The design canvas must have a width and height greater than zero.'
  }

  const keys = getPlaceholders(data).map((placeholder) => placeholder.key)
  const duplicate = keys.find((key, index) => keys.indexOf(key) !== index)
  if (duplicate) {
    return `Two placeholders share the name "${duplicate}". Each one needs its own.`
  }

  return null
}
